
import Vue from 'vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import mixins from 'vue-typed-mixins';

import createSlug from '@/utils/slug';
import { formatNumber } from '@/i18n';

import tableViewMixin from '@/components/mixins/table-view';
import NotificationBanner from '@/components/blocks/notification-banner.vue';
import TableListSkeleton from '@/components/skeletons/table-list.vue';
import Dropdown, { DropdownItems } from '@/components/controls/dropdown.vue';
import PaginationControl from '@/components/controls/pagination.vue';
import SubmitButton from '@/components/controls/submit-button.vue';
import { ReportsActions } from '@/store/modules/reports/types';
import FormInput from '@/components/controls/form-input.vue';
import SelectInput from '@/components/controls/select-input.vue';
import CountriesSelection from '@/components/controls/countries-selection.vue';
import Country from '@/store/models/country';

export default mixins(tableViewMixin).extend({
  name: 'ResourceDownloadsView',
  components: {
    NotificationBanner,
    TableListSkeleton,
    Dropdown,
    PaginationControl,
    SubmitButton,
    FormInput,
    SelectInput,
    CountriesSelection,
  },
  data() {
    return {
      interval: '30',
      start: '',
      end: '',
      countryIds: [] as number[],
    };
  },
  computed: {
    ...mapState(['countries']),
    selectedCountries: {
      get(): Country[] {
        return this.countries.filter((country: Country) => this.countryIds.indexOf(Number(country.id)) !== -1);
      },
      set(countries: Country[]) {
        this.countryIds = countries.map((country) => Number(country.id));
      },
    },
    ...mapState('statistics', ['downloads', 'downloadsPagination']),
    ...mapGetters('user/profile', ['isSuperAdmin', 'isAdmin']),
    ...mapGetters('statistics', {
      isLoading: 'downloadsIsLoading',
      isLoaded: 'downloadsIsLoaded',
      isEmpty: 'downloadsIsEmpty',
    }),
    dropdownItems() {
      const items: DropdownItems = {};
      if (this.isSuperAdmin) {
        items.report = {
          label: this.$t('Generate Report'),
          icon: 'document',
        };
      }
      return items;
    },
  },
  methods: {
    ...mapActions('reports', {
      requestReport: ReportsActions.REQUEST,
    }),
    formatNumber,
    createSlug,
    routeName() {
      const isClinicalSupport = this.$route.name === 'clinical-support-downloads';
      if (isClinicalSupport) return 'clinical-support-resource';
      return 'resource';
    },
    applyFilters() {
      const query: { days: string; start?: string; end?: string; country_ids?: string } = {
        days: this.interval,
      };

      if (this.interval === 'custom') {
        query.start = this.start;
        query.end = this.end;
      }

      if (this.countryIds.length) {
        query.country_ids = this.countryIds.join(',');
      }

      this.$router.push({
        name: this.$route.name === 'clinical-support-downloads' ? 'clinical-support-downloads' : 'resource-downloads',
        params: { page: '1' },
        query,
      });
    },
    handleDropdownClick(action: string): void {
      if ('report' === action) {
        this.requestReport();
      }
    },
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler(query) {
        const value = query.country_ids !== undefined ? query.country_ids : query.country_id;
        const values = Array.isArray(value) ? value : [value];
        this.countryIds = Array.from(new Set(
          values.join(',').split(',').map(Number).filter((id) => Number.isInteger(id) && id > 0),
        ));
        this.interval = query.days === '7' || query.days === 'custom' ? query.days : '30';
        this.start = typeof query.start === 'string' ? query.start : '';
        this.end = typeof query.end === 'string' ? query.end : '';
      },
    },
  },
});
